import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useMe, useRaffles, useScraperHistory, useScraperRouting, useWebhookCategories } from '../../../api/queries';
import { useSaveScraperRouting } from '../../../api/mutations';
import { Spark } from '../../../components/Spark';
import { Gauge } from '../../../components/Gauge';
import { RaffleRow } from '../../../components/RaffleRow';
import { formatDuration, formatInterval, relTime } from '../../../lib/time';
import type { Scraper } from '../../../api/types';

function Dm({ k, v }: { k: string; v: string | number }) {
  return (
    <div className="dm">
      <div className="k">{k}</div>
      <div className="v">{v}</div>
    </div>
  );
}

export function MetricsTab({ scraper }: { scraper: Scraper }) {
  const navigate = useNavigate();
  const { data: history } = useScraperHistory(scraper.name);
  const { data: raffles } = useRaffles({ scraper: scraper.name, limit: 5 });
  const { data: me } = useMe();
  const { data: categories } = useWebhookCategories();
  const { data: routing } = useScraperRouting(scraper.name);
  const saveRouting = useSaveScraperRouting();
  const [category, setCategory] = useState<string | null>(null);
  const [testOverride, setTestOverride] = useState('');
  const [prodOverride, setProdOverride] = useState('');

  // Real execution times, oldest-first — replaces the prototype's random series.
  const execSeries = (history ?? [])
    .slice(0, 24)
    .reverse()
    .map((r) => r.exec_time ?? 0)
    .filter((n) => Number.isFinite(n));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="detail-metrics">
        <Dm k="Health" v={scraper.health_score} />
        <Dm k="Found total" v={raffles?.total ?? '—'} />
        <Dm k="Fails in a row" v={scraper.consecutive_failures || '—'} />
        <Dm k="Avg runtime" v={formatDuration(scraper.avg_execution_time)} />
        <Dm k="Interval" v={formatInterval(scraper.interval_seconds)} />
        <Dm k="Last run" v={relTime(scraper.last_run)} />
        <Dm k="Runner" v={scraper.runner_alive === null ? 'in worker' : scraper.runner_alive ? 'online' : 'not reporting'} />
        <Dm k="Route" v={`${scraper.webhook_category}${scraper.webhook_override ? ' · override' : ''}`} />
        <Dm k="Deployment" v={`revision ${scraper.deployment_revision || '—'}`} />
      </div>

      <div className="pcard">
        <div className="head"><h3>Delivery route</h3><span className="spacer" /><span className="pill">{routing?.override_enabled ? 'override active' : 'category default'}</span></div>
        <div className="body pad" style={{ display: 'grid', gap: 8 }}>
          <div className="muted" style={{ fontSize: 12.5 }}>Primary alerts resolve from this category, then an optional encrypted per-scraper profile override.</div>
          {me?.is_super_admin ? <>
            <select className="input" value={category ?? routing?.category_slug ?? scraper.webhook_category} onChange={(e) => setCategory(e.target.value)}>
              {(categories ?? []).map((item) => <option key={item.slug} value={item.slug}>{item.name}</option>)}
            </select>
            <input className="input" placeholder="Replace test override (blank clears)" value={testOverride} onChange={(e) => setTestOverride(e.target.value)} />
            <input className="input" placeholder="Replace prod override (blank clears)" value={prodOverride} onChange={(e) => setProdOverride(e.target.value)} />
            <div><button className="btn sm" disabled={saveRouting.isPending} onClick={() => saveRouting.mutate({ name: scraper.name, category_slug: category ?? routing?.category_slug ?? scraper.webhook_category, overrides: { test: testOverride || null, prod: prodOverride || null } })}>Save route</button></div>
          </> : <div className="muted" style={{ fontSize: 12.5 }}>Only a super admin can change routing.</div>}
        </div>
      </div>

      {execSeries.length > 0 && (
        <div className="pcard">
          <div className="head">
            <h3>Run duration</h3>
            <span className="spacer" />
            <span className="mono-sm muted">last {execSeries.length} runs</span>
          </div>
          <div className="body pad">
            <Spark data={execSeries} color="var(--teal)" w={680} h={54} />
          </div>
        </div>
      )}

      {scraper.slow_ratio !== null && (
        <div className="res">
          <div className="rr">
            <span className="rk">Load</span>
            <div style={{ flex: 1 }}>
              <Gauge
                value={Math.min(scraper.slow_ratio * 100, 100)}
                color={scraper.slow_warning ? 'var(--warn)' : 'var(--accent)'}
              />
            </div>
            <span className="rv">{Math.round(scraper.slow_ratio * 100)}%</span>
          </div>
          <div className="muted" style={{ fontSize: 11.5 }}>
            Average runtime as a share of the poll interval.
            {scraper.slow_warning && ' This scraper is at risk of overrunning its schedule.'}
          </div>
        </div>
      )}

      {scraper.error && (
        <div className="err">
          {scraper.error_type && (
            <span className="pill red" style={{ marginRight: 6 }}>
              {scraper.error_type}
            </span>
          )}
          {scraper.error}
        </div>
      )}

      <div className="pcard">
        <div className="head">
          <h3>Raffles from this scraper</h3>
          <span className="spacer" />
          {raffles?.total ? <span className="pill">{raffles.total} total</span> : null}
        </div>
        <div className="body">
          {raffles?.items.length ? (
            raffles.items.map((r) => (
              <RaffleRow key={r.id} raffle={r} onClick={() => navigate(`/raffles/${r.id}`)} />
            ))
          ) : (
            <div className="frow" style={{ borderBottom: 'none' }}>
              <div className="main">
                <div className="m">No raffles captured yet.</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
